import { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import { useGetSamplesInfiniteQuery } from './useSample'
import { Sample, SampleStatusEnum } from '../types/types'
import { Filters } from './useFilters'
import { OwnerId } from '../utils/taskUtils'
import { getSampleBusinessDisplayName } from '../utils/sampleUtils'

type UseAllSamplesProps = {
  isEnabled: boolean
  includeEvents?: boolean
  ownerScope?: 'exact' | 'subtree'
  ownerIds?: OwnerId[]
  sampleIds?: string[]
  filters?: Filters<SampleStatusEnum>
  sort?: string
}

export const useAllSamples = ({
  isEnabled,
  includeEvents,
  ownerScope,
  ownerIds,
  sampleIds,
  filters,
  sort
}: UseAllSamplesProps) => {
  const pageSize = 2000
  const producerSearch = filters?.producerSearch?.trim().toLowerCase()
  const isMountedRef = useRef(true)
  const currentPageRef = useRef(0)
  const cancelAutoFetchRef = useRef(false)
  const [isAutoFetching, setIsAutoFetching] = useState(false)
  const { data, fetchNextPage, hasNextPage, isFetching, isError } =
    useGetSamplesInfiniteQuery({
      isEnabled,
      includeEvents,
      ownerScope,
      ownerIds: ownerIds?.map(String)?.sort() ?? [],
      sampleIds,
      productIds: filters?.productIds?.map(Number)?.sort(),
      matrixIds: filters?.matrixIds?.map(Number)?.sort(),
      sampntTypeIds: filters?.sampntTypeIds?.map(Number)?.sort(),
      planIds: filters?.planIds?.map(Number)?.sort(),
      taskIds: filters?.taskIds?.map(Number)?.sort(),
      substanceIds: filters?.substanceIds?.map(Number)?.sort(),
      userId: filters?.userId,
      status: filters?.status,
      result: filters?.result,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
      sort,
      size: pageSize
    })

  const samples = useMemo(() => {
    const allSamples =
      data?.pages
        .flatMap((page) => page.content ?? [])
        .filter((sample): sample is Sample => sample != null) ?? []

    const uniqueSamples = Array.from(
      new Map(allSamples.map((sample) => [sample.id, sample] as const)).values()
    )

    if (producerSearch == null || producerSearch.length === 0) {
      return uniqueSamples
    }

    return uniqueSamples.filter((sample) => {
      return [
        getSampleBusinessDisplayName(sample),
        sample.externalReferenceId,
        sample.oversightObject,
        sample.orgnr?.toString()
      ].some((value) => value?.toLowerCase().includes(producerSearch) ?? false)
    })
  }, [data?.pages, producerSearch])

  const fetchAllPages = useCallback(async () => {
    if (!isEnabled) return
    cancelAutoFetchRef.current = false
    setIsAutoFetching(true)
    try {
      let stillHasNext = hasNextPage
      while (stillHasNext && !cancelAutoFetchRef.current) {
        const result = await fetchNextPage()
        currentPageRef.current += 1
        stillHasNext = result.hasNextPage ?? false
      }
    } finally {
      if (isMountedRef.current) {
        setIsAutoFetching(false)
      }
    }
  }, [fetchNextPage, hasNextPage, isEnabled])

  useEffect(() => {
    // cleanup on unmount: cancel any ongoing auto-fetch and avoid setState after unmount
    return () => {
      isMountedRef.current = false
      cancelAutoFetchRef.current = true
    }
  }, [])

  useEffect(() => {
    // prevent re-trigger while already fetching
    if (isEnabled && hasNextPage && !isAutoFetching && !isFetching) {
      const autoFetch = async (): Promise<void> => {
        try {
          await fetchAllPages()
        } catch {
          // Query state already exposes fetch errors to consumers.
        }
      }
      autoFetch()
    }
  }, [isEnabled, hasNextPage, isAutoFetching, isFetching, fetchAllPages])

  const isPendingAutoFetch =
    isEnabled && !isFetching && !isAutoFetching && hasNextPage

  const isLoading =
    isEnabled && (isFetching || isAutoFetching || isPendingAutoFetch)

  const totalSamples = data?.pages[0]?.totalElements ?? 0
  const currentSamples =
    data?.pages?.reduce((sum, item) => sum + item.numberOfElements!, 0) ?? 0
  const progress = {
    value: currentSamples,
    max: Math.max(totalSamples, 1)
  }

  return {
    samples,
    isLoading,
    isComplete: isEnabled && !isLoading && !isError,
    isError,
    progress
  }
}
